import { useMemo } from 'react';
import {
  AssetHeader,
  HealthTimeline,
  EmptyState,
  TrendTrack,
  LinkedCursorProvider,
  type HealthPeriod,
} from '@/lib';
import type { ApiClients } from '@/api/client';
import type { TrendPoint } from '@/lib';
import {
  useAssetDetail,
  useAssetDiagnosis,
  useAssetSensorsHealth,
  useAssetHealthHistory,
  type HealthHistory,
} from './queries';

export type S3AssetDetailProps = {
  assetId: string;
  clients: ApiClients;
};

/**
 * S3 Asset Detail. The first screen built against the new chart stack
 * — composes AssetHeader (with topology strip) + HealthTimeline +
 * TrendTrack + a per-sensor table. Phase 4 (3/n).
 *
 * Loading is a coarse skeleton; once any one of the four queries
 * errors we surface the error in place. This matches the resilience
 * model used in S1 — partial data is still useful.
 */
export function S3AssetDetail({ assetId, clients }: S3AssetDetailProps) {
  const detail = useAssetDetail(clients, assetId);
  const diagnosis = useAssetDiagnosis(clients, assetId);
  const sensors = useAssetSensorsHealth(clients, assetId);
  const history = useAssetHealthHistory(clients, assetId, 30);

  const headerAsset = useMemo(() => {
    if (!detail.data) return null;
    const d = detail.data;
    return {
      id: d.asset_id,
      name: d.name,
      klass: d.asset_class,
      site: d.site_name,
      criticality: mapCriticality(d.criticality),
      health: d.current_health_score,
      thresholds: { warn: 70, critical: 30 },
    };
  }, [detail.data]);

  const trendPoints = useMemo<TrendPoint[]>(() => {
    // The gateway's health-history endpoint may return either the contract
    // `periods` shape ({ at, status }) or the anomaly-service `points` shape
    // ({ timestamp, value }). Support both and never assume the array exists.
    const h = history.data as
      | (HealthHistory & { points?: { timestamp: string; value: number }[] })
      | undefined;
    if (!h) return [];
    if (Array.isArray(h.periods)) {
      return h.periods.map((p) => ({
        t: new Date(p.at).getTime() / 1000,
        v: healthFromStatus(p.status),
      }));
    }
    if (Array.isArray(h.points)) {
      return h.points.map((p) => ({
        t: new Date(p.timestamp).getTime() / 1000,
        v: p.value,
      }));
    }
    return [];
  }, [history.data]);

  if (detail.isLoading) {
    return <S3Loading />;
  }
  if (detail.isError) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load asset"
        body={String(detail.error ?? 'asset detail failed')}
        cta={{ label: 'Retry', onClick: () => detail.refetch() }}
      />
    );
  }
  if (!detail.data || !headerAsset) {
    return (
      <EmptyState
        variant="empty"
        title="Asset not found"
        body={`No asset matches the id "${assetId}".`}
      />
    );
  }

  return (
    <LinkedCursorProvider tickSeconds={3600}>
      <div className="flex flex-col gap-sp-6">
        <AssetHeader
          asset={headerAsset}
          topology={
            detail.data.topology
              ? {
                  nodes: (detail.data.topology.nodes ?? []).map((n) => ({
                    id: n.id,
                    label: n.label,
                    kind: (n.kind as 'motor' | 'coupling' | 'pump' | 'bearing' | 'sensor' | undefined) ?? 'sensor',
                  })),
                  impactedIds: detail.data.topology.impacted_ids ?? [],
                }
              : undefined
          }
          freshness={{ at: new Date(history.dataUpdatedAt || Date.now()), tone: 'fresh' }}
        />

        <section
          aria-labelledby="diagnosis-heading"
          className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-6"
        >
          <h2
            id="diagnosis-heading"
            className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
          >
            Why this is flagged
          </h2>
          {diagnosis.isLoading ? (
            <p className="mt-sp-2 text-fs-body text-gray-700">Loading diagnosis…</p>
          ) : diagnosis.isError || !diagnosis.data ? (
            <p className="mt-sp-2 text-fs-body text-gray-700" role="alert">
              Diagnosis unavailable.
            </p>
          ) : (
            <>
              <p className="mt-sp-2 text-fs-statement text-gray-900">
                {diagnosis.data.headline}
              </p>
              <p className="mt-sp-2 text-fs-body text-gray-900">
                {diagnosis.data.narrative}
              </p>
            </>
          )}
        </section>

        <section aria-labelledby="trend-heading" className="flex flex-col gap-sp-3">
          <h2
            id="trend-heading"
            className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
          >
            30-day health trend
          </h2>
          <TrendTrack
            points={trendPoints}
            loading={history.isLoading}
            error={history.isError ? 'Failed to load history.' : null}
            ariaLabel={`30-day health trend for ${detail.data.name}`}
          />
        </section>

        <section aria-labelledby="timeline-heading" className="flex flex-col gap-sp-3">
          <h2
            id="timeline-heading"
            className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
          >
            Period status
          </h2>
          <HealthTimeline
            variant="30d"
            periods={(history.data?.periods ?? []).map(
              (p): HealthPeriod => ({
                at: new Date(p.at),
                status: p.status as HealthPeriod['status'],
              }),
            )}
          />
        </section>

        <section aria-labelledby="sensors-heading" className="flex flex-col gap-sp-3">
          <h2
            id="sensors-heading"
            className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
          >
            Sensors on this asset
          </h2>
          {sensors.isLoading ? (
            <p className="text-fs-body text-gray-700">Loading sensors…</p>
          ) : sensors.isError || !sensors.data ? (
            <p className="text-fs-body text-gray-700" role="alert">
              Sensor health unavailable.
            </p>
          ) : (sensors.data.sensors ?? []).length === 0 ? (
            <p className="text-fs-body text-gray-700">No sensors attached.</p>
          ) : (
            <ul className="flex flex-col gap-sp-2">
              {(sensors.data.sensors ?? []).map((s) => (
                <li
                  key={s.sensor_id}
                  className="flex items-center justify-between rounded-r-default border border-gray-300 bg-gray-50 p-sp-3"
                >
                  <div className="min-w-0">
                    <p className="text-fs-body font-medium text-gray-900">{s.sensor_id}</p>
                    <p className="text-fs-meta text-gray-700">
                      {s.position} · {s.type}
                    </p>
                  </div>
                  <div className="flex items-center gap-sp-2">
                    <span
                      data-status={s.status}
                      className="rounded-r-chip border border-gray-300 px-sp-2 py-sp-1 text-fs-meta font-medium text-gray-900"
                    >
                      {s.status}
                    </span>
                    <span className="text-fs-meta text-gray-700">{s.health}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </LinkedCursorProvider>
  );
}

function S3Loading() {
  return (
    <div className="flex flex-col gap-sp-6" aria-busy="true">
      <div className="h-32 animate-pulse rounded-r-card bg-gray-100" />
      <div className="h-24 animate-pulse rounded-r-card bg-gray-100" />
      <div className="h-32 animate-pulse rounded-r-card bg-gray-100" />
    </div>
  );
}

function mapCriticality(c: string): 'low' | 'medium' | 'high' {
  if (c === 'low' || c === 'medium' || c === 'high') return c;
  if (c === 'critical') return 'high';
  return 'medium';
}

function healthFromStatus(s: string): number {
  switch (s) {
    case 'critical':
      return 15;
    case 'warn':
      return 40;
    case 'watch':
      return 60;
    default:
      return 85;
  }
}
